import { HttpContextToken, HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, from, switchMap, throwError } from 'rxjs';
import Cookies from 'js-cookie';
import { AuthStateService } from '../services/auth-state.service';
import { AuthService } from '../services/auth.service';
import { LOCAL_AUTH_FAILURE } from './local-auth-failure';

const RETRY_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

function withAuthHeaders(req: HttpRequest<unknown>, token: string | null): HttpRequest<unknown> {
  const setHeaders: Record<string, string> = {};
  if (token) {
    setHeaders['Authorization'] = `Bearer ${token}`;
    const csrfToken = Cookies.get('csrf_token');
    if (csrfToken) setHeaders['X-CSRF-Token'] = csrfToken;
  }

  return Object.keys(setHeaders).length
    ? req.clone({ withCredentials: true, setHeaders })
    : req.clone({ withCredentials: true });
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authState = inject(AuthStateService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (req.headers.has('__skipAuthInterceptor')) {
    return next(req.clone({ withCredentials: true, headers: req.headers.delete('__skipAuthInterceptor') }));
  }

  const authReq = withAuthHeaders(req, authState.token);

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 || req.context.get(RETRY_AFTER_REFRESH) || req.context.get(LOCAL_AUTH_FAILURE)) return throwError(() => err);

      return from(authService.refreshToken().catch(() => null)).pipe(
        switchMap(newToken => {
          if (!newToken) {
            authState.logout();
            router.navigate(['/login']);
            return throwError(() => err);
          }

          const retryReq = req.clone({
            context: req.context.set(RETRY_AFTER_REFRESH, true),
          });
          return next(withAuthHeaders(retryReq, newToken));
        })
      );
    })
  );
};
